import React from 'react';
import { carouselSkipLinks, carouselSkipLink, combineCarouselClasses } from './carousel.styles';
import type { CarouselSkipLinksProps } from './types';

/**
 * CarouselSkipLinks - Accessibility skip navigation for carousel
 *
 * Provides skip links that allow keyboard and screen reader users to quickly
 * navigate to the beginning or end of a carousel without having to tab through
 * all items. Links are visually hidden until focused.
 *
 * @example
 * ```tsx
 * <Carousel.SkipLinks
 *   onSkipToStart={handleSkipToStart}
 *   onSkipToEnd={handleSkipToEnd}
 * />
 * ```
 */
export const CarouselSkipLinks = React.forwardRef<HTMLDivElement, CarouselSkipLinksProps>(
  (
    {
      onSkipToStart,
      onSkipToEnd,
      skipToStartLabel = 'Skip to beginning of carousel',
      skipToEndLabel = 'Skip to end of carousel',
      className,
      ...restProps
    },
    ref,
  ) => {
    const handleSkipToStart = (event: React.MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();
      if (onSkipToStart) {
        onSkipToStart();
      }
    };

    const handleSkipToEnd = (event: React.MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();
      if (onSkipToEnd) {
        onSkipToEnd();
      }
    };

    const combinedClassName = combineCarouselClasses(carouselSkipLinks, className);

    return (
      <div ref={ref} className={combinedClassName} {...restProps}>
        {onSkipToEnd && (
          <a href="#carousel-end" className={carouselSkipLink} onClick={handleSkipToEnd}>
            {skipToEndLabel}
          </a>
        )}
        {onSkipToStart && (
          <a href="#carousel-start" className={carouselSkipLink} onClick={handleSkipToStart}>
            {skipToStartLabel}
          </a>
        )}
      </div>
    );
  },
);

CarouselSkipLinks.displayName = 'CarouselSkipLinks';
